import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import '../../styles/global.css';

interface ImportResult {
  filename: string;
  success: boolean;
  message: string;
  imported: number;
  file_id?: number;
  details?: string[];
  errors?: string[];
  skipped?: number;
  production_line_id?: number;
  week?: string;
}

interface ProductionLineOption {
  id: number;
  name: string;
  is_active?: boolean;
}

/** Week number and production line chosen in the import window for one file. */
interface FileTarget {
  week: string;
  lineId: string;
}

const isWeekValid = (week: string) => /^\d{1,2}$/.test(week.trim()) && +week >= 1 && +week <= 53;

const ImportProduction: React.FC = () => {
  const { t } = useLanguage();
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [results, setResults] = useState<ImportResult[]>([]);
  const [lines, setLines] = useState<ProductionLineOption[]>([]);
  // Import window: one week + line per selected file; null while closed.
  const [targets, setTargets] = useState<FileTarget[] | null>(null);
  const [targetError, setTargetError] = useState('');

  useEffect(() => {
    api
      .get('/production-lines')
      .then((res) => {
        const active = (res.data as ProductionLineOption[]).filter((l) => l.is_active !== false);
        // Already in the order the manager set in "Linie produkcyjne".
        setLines(active);
      })
      .catch(() => setLines([]));
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      setFiles(selectedFiles);
      setResults([]);
      setMessage('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0) {
      setMessage(t('import.selectFiles'));
      setIsSuccess(false);
      return;
    }
    setTargetError('');
    setTargets(files.map(() => ({ week: '', lineId: '' })));
  };

  // Typing the week of one file fills it in for the files that have none yet (usually one week per import).
  const setTarget = (index: number, patch: Partial<FileTarget>) => {
    setTargets((current) =>
      current
        ? current.map((target, i) =>
            i === index || (patch.week !== undefined && target.week === '')
              ? { ...target, ...(i === index ? patch : { week: patch.week }) }
              : target
          )
        : current
    );
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targets || targets.some((target) => !isWeekValid(target.week) || !target.lineId)) {
      setTargetError(t('import.targetMissing'));
      return;
    }
    const chosen = targets;
    setTargets(null);
    setLoading(true);
    setMessage('');
    setResults([]);

    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append('files[]', file);
      formData.append(`targets[${index}][week]`, String(parseInt(chosen[index].week, 10)));
      formData.append(`targets[${index}][production_line_id]`, chosen[index].lineId);
    });

    try {
      const response = await api.post('/production-items/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.results) {
        setResults(response.data.results);
        const successCount = response.data.success_count || 0;
        const totalImported = response.data.total_imported || 0;

        const msg =
          files.length === 1
            ? t('import.successSingle', { count: totalImported.toString() })
            : t('import.successMulti', {
                success: successCount.toString(),
                total: files.length.toString(),
                imported: totalImported.toString(),
              });
        setMessage(msg);
      } else {
        setMessage(
          t('import.successSingle', {
            count: (response.data.imported || response.data.total_imported || 0).toString(),
          })
        );
      }
      setIsSuccess(true);

      setFiles([]);
      const fileInput = document.getElementById('file-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error: any) {
      const validation = error.response?.data?.errors as Record<string, string[]> | undefined;
      setMessage(
        (validation && Object.values(validation)[0]?.[0]) || error.response?.data?.message || t('import.importFailed')
      );
      setIsSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header" style={{ padding: '8px 0', marginBottom: 20 }}>
        <h1
          style={{
            color: '#2d3748',
            background: 'none',
            WebkitBackgroundClip: 'initial',
            WebkitTextFillColor: '#2d3748',
            backgroundClip: 'initial',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ WebkitTextFillColor: 'initial' }}>📥</span>
          {t('import.title')}
        </h1>
      </div>

      <div
        style={{
          marginBottom: 20,
          padding: '14px 16px',
          backgroundColor: '#eef2ff',
          border: '1px solid #c7d2fe',
          borderRadius: 8,
          color: '#2d3748',
        }}
      >
        <strong style={{ color: '#667eea' }}>💡 {t('import.hint')}:</strong> {t('import.flowHint')}
      </div>

      <div className="card" style={{ maxWidth: 720 }}>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor="file-input"
              style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}
            >
              {t('import.selectFilesLabel')} *
            </label>
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls"
              multiple
              onChange={handleFileChange}
              className="input"
              style={{ cursor: 'pointer', padding: 10 }}
            />
            <small style={{ display: 'block', marginTop: 6, color: '#718096' }}>
              {t('import.multiSelectHint')}
            </small>
          </div>

          {files.length > 0 && (
            <div
              style={{
                marginBottom: 20,
                padding: '12px 16px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #e1e8ed',
                borderRadius: 8,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 8, color: '#2d3748' }}>
                {t('import.selectedFiles')} ({files.length}):
              </div>
              <ul style={{ margin: 0, paddingLeft: 20, color: '#4a5568' }}>
                {files.map((file, index) => (
                  <li key={index} style={{ marginBottom: 2 }}>
                    📄 {file.name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {message && (
            <div
              style={{
                marginBottom: 20,
                padding: '12px 16px',
                backgroundColor: isSuccess ? '#d1fae5' : '#fee2e2',
                color: isSuccess ? '#065f46' : '#991b1b',
                borderRadius: 8,
                fontWeight: 500,
              }}
            >
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || files.length === 0}
            className="btn"
            style={{
              padding: '12px 24px',
              fontSize: 15,
              backgroundColor: loading || files.length === 0 ? '#cbd5e0' : '#667eea',
              color: 'white',
              fontWeight: 600,
              cursor: loading || files.length === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {loading
              ? t('import.importing')
              : files.length > 0
              ? `${t('import.import')} (${files.length})`
              : t('import.import')}
          </button>
        </form>
      </div>

      {targets && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
          onClick={() => setTargets(null)}
        >
          <form
            onSubmit={handleConfirm}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-target-title"
            style={{
              background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 560,
              maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
            }}
          >
            <h2 id="import-target-title" style={{ margin: '0 0 6px', fontSize: 20, color: '#2d3748' }}>
              {t('import.targetTitle')}
            </h2>
            <p style={{ margin: '0 0 16px', fontSize: 14, color: '#4a5568' }}>{t('import.targetHint')}</p>

            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                style={{ padding: '12px 0', borderTop: index ? '1px solid #e1e8ed' : 'none' }}
              >
                <div style={{ fontWeight: 600, color: '#2d3748', marginBottom: 8, overflowWrap: 'anywhere' }}>
                  📄 {file.name}
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <label style={{ flex: '0 0 110px', fontSize: 13, fontWeight: 600, color: '#4a5568' }}>
                    {t('import.week')}
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={53}
                      autoFocus={index === 0}
                      value={targets[index].week}
                      placeholder={t('import.weekPlaceholder')}
                      onChange={(e) => setTarget(index, { week: e.target.value })}
                      className="input"
                      style={{ marginTop: 4, width: '100%', boxSizing: 'border-box' }}
                    />
                  </label>
                  <label style={{ flex: '1 1 220px', fontSize: 13, fontWeight: 600, color: '#4a5568' }}>
                    {t('import.line')}
                    <select
                      value={targets[index].lineId}
                      onChange={(e) => setTarget(index, { lineId: e.target.value })}
                      className="input"
                      style={{ marginTop: 4, width: '100%', boxSizing: 'border-box' }}
                    >
                      <option value="">{t('import.linePlaceholder')}</option>
                      {lines.map((line) => (
                        <option key={line.id} value={line.id}>
                          {line.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
            ))}

            {targetError && (
              <div
                role="alert"
                style={{ margin: '8px 0', padding: '10px 12px', borderRadius: 8, background: '#fee2e2', color: '#991b1b' }}
              >
                {targetError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn"
                onClick={() => setTargets(null)}
                style={{ padding: '10px 20px', background: '#f1f5f9', color: '#4a5568', fontWeight: 600 }}
              >
                {t('import.cancel')}
              </button>
              <button
                type="submit"
                className="btn"
                style={{ padding: '10px 20px', background: '#667eea', color: '#fff', fontWeight: 600 }}
              >
                {t('import.confirm')}
              </button>
            </div>
          </form>
        </div>
      )}

      {results.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ color: '#2d3748', marginBottom: 16 }}>{t('import.importDetails')}</h2>
          {results.map((result, index) => (
            <div
              key={index}
              className="card"
              style={{
                marginBottom: 14,
                padding: 16,
                borderLeft: `4px solid ${result.success ? '#10b981' : '#ef4444'}`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 8,
                  fontSize: 16,
                  fontWeight: 600,
                  color: result.success ? '#065f46' : '#991b1b',
                }}
              >
                <span>{result.success ? '✅' : '❌'}</span>
                <span>{result.filename}</span>
              </div>
              <div style={{ color: '#4a5568', marginBottom: 8 }}>
                {result.message}
                {result.success && ` (${result.imported} ${t('import.itemsImported')}`}
                {result.success && result.skipped && result.skipped > 0 && `, ${result.skipped} ${t('import.skipped')}`}
                {result.success && ')'}
              </div>

              {result.details && result.details.length > 0 && (
                <div
                  style={{
                    marginTop: 10,
                    padding: '10px 14px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: 6,
                    fontSize: 13,
                  }}
                >
                  <strong style={{ display: 'block', marginBottom: 6, color: '#4a5568' }}>
                    {t('import.details')}:
                  </strong>
                  <ul style={{ margin: 0, paddingLeft: 20, color: '#4a5568' }}>
                    {result.details.map((detail, detailIndex) => (
                      <li key={detailIndex} style={{ marginBottom: 3, whiteSpace: 'pre-wrap' }}>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.errors && result.errors.length > 0 && (
                <div
                  style={{
                    marginTop: 10,
                    padding: '10px 14px',
                    backgroundColor: '#fef2f2',
                    borderRadius: 6,
                    fontSize: 13,
                  }}
                >
                  <strong style={{ display: 'block', marginBottom: 6, color: '#991b1b' }}>
                    {t('import.errors')}:
                  </strong>
                  <ul style={{ margin: 0, paddingLeft: 20, color: '#991b1b' }}>
                    {result.errors.map((error, errorIndex) => (
                      <li key={errorIndex} style={{ marginBottom: 3 }}>
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImportProduction;
